// Wann wird die Statuszeile eines Listings weiter beobachtet, und in welchem Takt?
//
// Bewusst DB- und DOM-frei, damit die Regeln ohne Browser testbar sind (Muster:
// `lib/price-diff.ts` im Worker, `lib/publish-button.ts` hier).
//
// ANLASS (2026-08-20): Der Betreiber sah dauerhaft "Kaufland prüft die Produktdaten. Letzte
// Rückmeldung: MISSING_ATTRIBUTES -- fehlende Attribute: Beschreibung", obwohl die
// Beschreibung mit 1840 Zeichen eingereicht war und Kaufland um 19:56 freigegeben hatte.
//
// Das Backend hatte die ganze Zeit recht -- es pollte weiter und setzte `published`.
// Eingefroren war die ANZEIGE, an zwei Stellen:
//
//   1. `const nichtsMehrZuSehen = row.status === 'validating' && reason !== null;`
//      Sobald Kaufland zum ersten Mal irgendeinen Wartegrund lieferte, stellte die
//      Oberfläche die Beobachtung ein -- und liess genau diesen Satz für immer stehen. Der
//      Name verrät die Absicht ("nichts mehr zu sehen"); ein Wartegrund ist aber genau der
//      Zustand, in dem man weiterschauen muss.
//   2. `MAX_TICKS = 40` bei 3 s Takt = 2 Minuten Beobachtungsdauer. Gemessen an 13
//      Einreichungen (`kaufland.listings`) dauert eine Freigabe 32 Sekunden bis 31 Minuten.
//      Die Oberfläche gab also regelmässig auf, bevor Kaufland antwortete.

/** Zustände, nach denen es nichts mehr zu beobachten gibt. */
const TERMINAL = new Set(['published', 'error', 'archived']);

/** Obergrenze der Beobachtung. Über dem langsamsten gemessenen Fall (31 Min), damit die
 *  Anzeige nicht aufgibt, bevor Kaufland antwortet. */
export const WATCH_MAX_MS = 40 * 60_000;

/**
 * Gestaffelter Takt: dicht am Anfang, sparsam später.
 *
 * Die Hälfte der gemessenen Freigaben kam binnen 60 Sekunden -- dort lohnt sich Dichte.
 * Danach kostet jede Sekunde Takt nur noch Abfragen ohne Erkenntnisgewinn.
 */
const STAFFEL: ReadonlyArray<{ abMs: number; delayMs: number }> = [
  { abMs: 0,        delayMs: 2_000 },
  { abMs: 30_000,   delayMs: 5_000 },
  { abMs: 120_000,  delayMs: 15_000 },
  { abMs: 300_000,  delayMs: 30_000 },
];

export interface TaktEingabe {
  status: string;
  /** Kauflands Wartegrund (`kaufland_status_reason`). Beeinflusst den Takt NICHT --
   *  siehe Punkt 1 im Dateikopf. */
  reason: string | null;
  /** Zeit seit Beginn der Beobachtung. */
  verstrichenMs: number;
}

export interface Takt {
  weiter: boolean;
  delayMs: number;
}

/**
 * Weiter beobachten? Und wenn ja, wie lange bis zur nächsten Abfrage?
 *
 * `reason` steht bewusst im Eingabe-Typ, wird aber nicht ausgewertet: Der alte Code brach
 * genau daran ab. Das Feld hier zu führen und zu ignorieren, macht sichtbar, dass die
 * Entscheidung getroffen wurde -- statt sie durch Weglassen unsichtbar zu machen.
 *
 * Pure -- keine IO.
 */
export function naechsterTakt(e: TaktEingabe): Takt {
  if (TERMINAL.has(e.status)) return { weiter: false, delayMs: 0 };
  if (e.verstrichenMs >= WATCH_MAX_MS) return { weiter: false, delayMs: 0 };

  let delayMs = STAFFEL[0]!.delayMs;
  for (const stufe of STAFFEL) {
    if (e.verstrichenMs >= stufe.abMs) delayMs = stufe.delayMs;
  }
  return { weiter: true, delayMs };
}

/**
 * Wie alt ist der angezeigte Stand? Macht Veraltetes sichtbar, statt es zu verschweigen --
 * der Kern der Beschwerde vom 2026-08-20 war eine Meldung ohne erkennbares Alter.
 *
 * Pure -- keine IO.
 */
export function statusAlter(alterMs: number | null): string | null {
  if (alterMs === null || !Number.isFinite(alterMs)) return null;
  if (alterMs < 60_000) return 'gerade eben';
  return `vor ${Math.floor(alterMs / 60_000)} Min.`;
}
